import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getTicketCountLoading, getOverDueCountLoading } from 'src/store/actions';
import { RootReducerState } from 'src/store/reducers';
import { Tooltip } from 'primereact/tooltip';
import FeatherIcon from 'feather-icons-react';
import { useNavigate } from 'react-router-dom';
import { ROUTER } from 'src/constants/routes';

interface TicketCountItem {
  priority: 'high' | 'medium' | 'low' | 'overDue';
  count: number;
}

const priorityConfig = {
  high: {
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
    label: 'H',
  },
  medium: {
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
    label: 'M',
  },
  low: {
    color: '#22c55e',
    bgColor: 'rgba(34, 197, 94, 0.1)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
    label: 'L',
  },
};

const TicketCount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { profile } = useSelector((state: RootReducerState) => state.profilesReducer);
  const { ticketCount, overDueCount } = useSelector(
    (state: RootReducerState) => state.ticketSystemReducer
  );

  useEffect(() => {
    if (profile) {
      const isAdmin = profile?.extraProperties?.IsAdmin || profile?.extraProperties?.IsSuperAdmin || false;
      dispatch(getTicketCountLoading(isAdmin));
      dispatch(getOverDueCountLoading(isAdmin));
    }
  }, [dispatch, profile]);

  const handleClick = (priority?: string, isOverDue?: boolean) => {
    if (priority) {
      navigate(`${ROUTER.TICKET_SYSTEM}?priority=${priority}`);
    } else if (isOverDue) {
      navigate(`${ROUTER.TICKET_SYSTEM}?isCompletedWithinDueDate=false`);
    } else {
      navigate(ROUTER.TICKET_SYSTEM);
    }
  };

  const getCountByPriority = (priority: string) => {
    if (!ticketCount || !Array.isArray(ticketCount)) return 0;
    return ticketCount.find((item: any) =>
      item.priority?.toLowerCase() === priority.toLowerCase()
    )?.count || 0;
  };

  const ticketData = [
    { label: 'High', count: getCountByPriority('high'), bgColor: '#fee2e2', textColor: '#991b1b', borderColor: '#ef4444' },
    { label: 'Medium', count: getCountByPriority('medium'), bgColor: '#ffedd5', textColor: '#9a3412', borderColor: '#f97316' },
    { label: 'Low', count: getCountByPriority('low'), bgColor: '#fef9c3', textColor: '#854d0e', borderColor: '#eab308' },
    { label: 'Over Due', count: overDueCount?.[0]?.count || 0, bgColor: '#f1f5f9', textColor: '#334155', borderColor: '#64748b' },
  ];

  return (
    <div
      className="d-flex align-items-center gap-2"
      onClick={() => handleClick()}
      style={{ cursor: 'pointer', paddingRight: '15px' }}
    >
      {ticketData.map((item, index) => (
        <div
          key={index}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={(e) => {
            e.stopPropagation();
            if (item.label !== 'Over Due') {
              handleClick(item.label.toLowerCase());
            } else {
              handleClick(undefined, true);
            }
          }}
          style={{
            backgroundColor: item.bgColor,
            color: item.textColor,
            borderRadius: '4px',
            padding: '6px 0',
            width: '72px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            border: `1.5px solid ${item.borderColor}`,
            boxShadow: hoveredIndex === index
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease-in-out',
            transform: hoveredIndex === index ? 'translateY(-2px)' : 'translateY(0)',
          }}
        >
          <div style={{
            fontSize: '9.5px',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '1px'
          }}>
            {item.label}
          </div>
          <div style={{
            fontSize: '17px',
            fontWeight: 900,
            lineHeight: '1'
          }}>
            {item.count}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketCount;
